import React from 'react';
import {
  Group,
  type GroupProps,
  Switch as RACSwitch,
  type SwitchProps as RACSwitchProps,
} from 'react-aria-components';
import { twMerge } from 'tailwind-merge';
import { DescriptionContext, DescriptionProvider, LabeledGroup } from './field';
import { composeTailwindRenderProps, focusVisibleOutlineStyle, groupBoxStyle } from './utils';

export function SwitchGroup({ className, ...props }: GroupProps) {
  return (
    <LabeledGroup>
      <Group {...props} className={composeTailwindRenderProps(className, groupBoxStyle)}></Group>
    </LabeledGroup>
  );
}

export function Switches({ className, ...props }: JSX.IntrinsicElements['div']) {
  return (
    <div
      data-ui="box"
      className={twMerge(
        'flex flex-col',
        // When any switch item has description, apply all `font-medium` to all switch item labels
        'has-data-[ui=description]:[&_label]:font-medium',
        className,
      )}
      {...props}
    />
  );
}

export function SwitchField({ className, ...props }: JSX.IntrinsicElements['div']) {
  return (
    <DescriptionProvider>
      <div
        {...props}
        data-ui="field"
        className={twMerge(
          'group flex flex-col gap-y-1',
          'has-[label[data-position=left]]:[&_[data-ui=description]:not([class*=pe-])]:pe-[calc(theme(width.8)+16px)]',
          'has-[label[data-position=right]]:[&_[data-ui=description]:not([class*=ps-])]:ps-[calc(theme(width.8)+12px)]',
          'has-data-[ui=description]:[&_label]:font-medium',
          'has-[label[data-disabled]]:**:data-[ui=description]:opacity-50',
          className,
        )}
      />
    </DescriptionProvider>
  );
}

interface SwitchProps extends RACSwitchProps {
  labelPosition?: 'left' | 'right';
  plain?: boolean;
}
export const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  { labelPosition = 'right', children, plain, ...props },
  ref,
) {
  const descriptionContext = React.useContext(DescriptionContext);
  const innerRef = React.useRef<HTMLInputElement | null>(null);
  React.useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);

  return (
    <RACSwitch
      {...props}
      aria-describedby={descriptionContext?.['aria-describedby']}
      data-position={labelPosition}
      className={composeTailwindRenderProps(props.className, [
        'group flex items-center gap-x-3',
        'data-[position=left]:flex-row-reverse',
        'data-[position=left]:justify-between',
        !plain && 'text-base/6 sm:text-sm/6',
        'disabled:opacity-50',
      ])}
      inputRef={innerRef}
    >
      {(renderProps) => (
        <>
          <div
            className={twMerge(
              'flex h-5 w-8 shrink-0 cursor-default items-center rounded-full border px-0.5 shadow-inner transition duration-200 ease-in-out',
              !plain && 'bg-zinc-200 dark:bg-transparent',
              'group-selected:border-accent group-selected:bg-accent group-selected:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)] group-selected:dark:border-0 group-selected:dark:bg-accent',
              'disabled:opacity-50',
              focusVisibleOutlineStyle,
            )}
          >
            <span
              className={twMerge(
                'h-[0.95rem] w-[0.95rem] translate-x-0 transform rounded-full border bg-white shadow-xs transition duration-200 ease-in-out',
                'group-selected:rtl:-translate-x-[78%] group-selected:translate-x-[78%] group-selected:border-accent',
              )}
            />
          </div>
          {typeof children === 'function' ? children(renderProps) : children}
        </>
      )}
    </RACSwitch>
  );
});
